import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDumbbell, faUtensils, faCommentDots, faMobileAlt, faUser,
  faComments, faLifeRing, faCheck, faSpinner, faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import s from "./StudentReportModal.module.css";
import ModalHeader from "./ModalHeader";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useScrollFade } from "../hooks/useScrollFade";

// ─── Fluxo professor ────────────────────────────────────────────────────────
const TAGS_PROFESSOR = {
  treino: [
    "Dor durante o exercício",
    "Sem equipamento",
    "Exercício muito difícil",
    "Treino muito longo",
    "Falta de tempo",
    "Lesão / desconforto",
    "Movimentos sem clareza",
  ],
  dieta: [
    "Dificuldade de seguir",
    "Alimentos indisponíveis",
    "Restrição alimentar",
    "Dieta muito restritiva",
    "Sem apetite",
    "Custo dos alimentos",
  ],
  outro: [
    "Dúvida sobre progresso",
    "Motivação baixa",
    "Problema no app",
    "Sugestão de melhoria",
  ],
};

const CATEGORIES_PROFESSOR = [
  { id: "treino", icon: faDumbbell,    label: "Treino" },
  { id: "dieta",  icon: faUtensils,    label: "Dieta"  },
  { id: "outro",  icon: faCommentDots, label: "Outro"  },
];

// ─── Fluxo self-service ─────────────────────────────────────────────────────
const TAGS_SELF = {
  treino: [
    "Dúvida sobre um exercício",
    "Treino muito difícil",
    "Treino muito fácil",
    "Lesão / desconforto",
    "Não consigo executar o movimento",
    "Falta de equipamento",
  ],
  app: [
    "Erro no aplicativo",
    "Sugestão de melhoria",
    "Dúvida sobre pontuação",
    "Problema com meu cadastro",
  ],
  outro: [
    "Motivação baixa",
    "Dúvida sobre progressão",
    "Quero falar com alguém",
    "Outro assunto",
  ],
};

const CATEGORIES_SELF = [
  { id: "treino", icon: faDumbbell,   label: "Treino"      },
  { id: "app",    icon: faMobileAlt,  label: "App / Conta" },
  { id: "outro",  icon: faCommentDots, label: "Outro"      },
];


// ─── Fluxo onboarding (google, sem professor) ───────────────────────────────
const TAGS_ONBOARDING = {
  professor: [
    "Quero cadastrar alunos",
    "Como funciona para professores",
    "Dúvida sobre planos e preços",
    "Quero saber mais",
  ],
  plataforma: [
    "Como funciona o app",
    "Problema no login",
    "Dúvida sobre minha conta",
    "Sugestão de melhoria",
  ],
  outro: [
    "Dúvida geral",
    "Preciso de ajuda",
    "Outro assunto",
  ],
};

const CATEGORIES_ONBOARDING = [
  { id: "professor",  icon: faUser,        label: "Sou professor"  },
  { id: "plataforma", icon: faMobileAlt,   label: "Sobre o app"    },
  { id: "outro",      icon: faCommentDots, label: "Outro"          },
];

export default function StudentReportModal({ onClose, isSelfService = false, isOnboarding = false }) {
  useBodyScrollLock();
  const { scrollRef, canScrollMore, check } = useScrollFade();
  const [step, setStep]         = useState(1);
  const [category, setCategory] = useState(null);
  const [tags, setTags]         = useState([]);
  const [description, setDesc]  = useState("");
  const [loading, setLoading]   = useState(false);
  const [done, setDone]         = useState(false);

  const CATEGORIES = isOnboarding ? CATEGORIES_ONBOARDING : (isSelfService ? CATEGORIES_SELF : CATEGORIES_PROFESSOR);
  const TAGS       = isOnboarding ? TAGS_ONBOARDING       : (isSelfService ? TAGS_SELF       : TAGS_PROFESSOR);

  function toggleTag(t) {
    setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  }

  async function handleSubmit() {
    if (!description.trim()) return;
    setLoading(true);
    try {
      await api.post("/student-reports", {
        category,
        tags,
        description: description.trim(),
      });
      setDone(true);
    } catch (e) {
      console.error("[StudentReportModal] erro:", e);
    } finally {
      setLoading(false);
    }
  }

  const headerTitle = isOnboarding ? "Fale conosco" : (isSelfService ? "Precisa de ajuda?" : "Fale com seu professor");
  const headerIcon  = isOnboarding ? faCommentDots : (isSelfService ? faLifeRing : faComments);
  const successMsg  = isOnboarding
    ? "Recebemos sua mensagem e entraremos em contato em breve."
    : (isSelfService ? "Nossa equipe recebeu seu contato e responderá em breve." : "Seu professor foi notificado e vai analisar sua dificuldade em breve.");

  return (
    <div className={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal}>
        <ModalHeader onClose={onClose} />

        <div className={s.header}>
          <div className={s.headerLeft}>
            <span className={s.headerIcon}><FontAwesomeIcon icon={headerIcon} /></span>
            <h2 className={s.headerTitle}>{headerTitle}</h2>
          </div>
        </div>

        {!done && (
          <div className={s.steps}>
            {[1, 2].map(n => (
              <div key={n} className={`${s.stepDot} ${step >= n ? s.stepDotActive : ""}`} />
            ))}
          </div>
        )}

        <div className={s.body} ref={scrollRef} onScroll={check}>
          {done ? (
            <div className={s.success}>
              <div className={s.successIcon}><FontAwesomeIcon icon={faCheck} /></div>
              <h3 className={s.successTitle}>Mensagem enviada!</h3>
              <p className={s.successMsg}>{successMsg}</p>
              <button className={s.successClose} onClick={onClose}>Fechar</button>
            </div>

          ) : step === 1 ? (
            <>
              <p className={s.categoryLabel}>
                {isOnboarding ? "Como podemos ajudar?" : (isSelfService ? "Sobre o que você precisa de ajuda?" : "Qual é a dificuldade?")}
              </p>
              <div className={s.categoryGrid}>
                {CATEGORIES.map(c => (
                  <button
                    key={c.id}
                    className={`${s.categoryCard} ${category === c.id ? s.categoryCardActive : ""}`}
                    onClick={() => setCategory(c.id)}
                  >
                    <FontAwesomeIcon icon={c.icon} className={s.categoryEmoji} />
                    {c.label}
                  </button>
                ))}
              </div>
            </>

          ) : (
            <>
              <p className={s.sectionLabel}>Selecione o que se aplica (opcional)</p>
              <div className={s.tagsWrap}>
                {(TAGS[category] || []).map(t => (
                  <button
                    key={t}
                    className={`${s.tag} ${tags.includes(t) ? s.tagActive : ""}`}
                    onClick={() => toggleTag(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <label htmlFor="report-description" className={s.descLabel}>
                {isSelfService ? "Descreva o que precisa" : "Descreva em detalhes"}{" "}
                <span style={{ color: "var(--feedback-error)" }}>*</span>
              </label>
              <textarea
                id="report-description"
                className={s.textarea}
                placeholder={
                  isSelfService
                    ? "Conte o que está acontecendo. Nossa equipe vai te responder em breve..."
                    : "Conte o que está acontecendo, quando percebeu, o que tentou fazer..."
                }
                value={description}
                onChange={e => setDesc(e.target.value)}
                maxLength={1000}
              />
              <p className={s.required}>{description.length}/1000 caracteres</p>
            </>
          )}
          {canScrollMore && <div className={s.scrollFade} aria-hidden="true" />}
        </div>

        {!done && (
          <div className={s.footer}>
            {step === 2 && (
              <button className={s.backBtn} onClick={() => setStep(1)}>
                <FontAwesomeIcon icon={faChevronLeft} /> Voltar
              </button>
            )}
            {step === 1 ? (
              <button className={s.nextBtn} disabled={!category} onClick={() => setStep(2)}>
                Continuar →
              </button>
            ) : (
              <button
                className={s.nextBtn}
                disabled={!description.trim() || loading}
                onClick={handleSubmit}
              >
                {loading ? <><FontAwesomeIcon icon={faSpinner} spin /> Enviando…</> : "Enviar mensagem"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
